import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Purchases,
  Package,
  CustomerInfo,
  PurchasesError,
  ErrorCode,
} from '@revenuecat/purchases-js';
import { useQuery, useReactiveVar } from '@apollo/client/react';
import { env } from '@/lib/env';
import { logger } from '@/lib/logger';
import { CurrentUserDocument } from '@/features/graphql/generated/graphql';
import { authVar } from '@/lib/apollo/auth';
import { resolveTier } from '@fytness/shared';
import { tierVar } from '@/lib/powersync/tier';
import {
  CREDITS_OFFERING,
  ENTITLEMENTS,
  purchasesContext,
  PurchaseResult,
  PurchasesContextValue,
  SubscriptionPackage,
} from './Purchases.types';

export * from './Purchases.types';

/**
 * Formatted price is exposed under slightly different shapes across
 * `@revenuecat/purchases-js` versions (`price` vs `currentPrice`). Read it
 * defensively so a minor SDK bump can't blank the price out.
 */
const priceStringOf = (pkg: Package): string => {
  const product = pkg.webBillingProduct as unknown as {
    price?: { formattedPrice?: string };
    currentPrice?: { formattedPrice?: string };
  };
  return (
    product?.price?.formattedPrice ??
    product?.currentPrice?.formattedPrice ??
    ''
  );
};

const toSubscriptionPackage = (pkg: Package): SubscriptionPackage => ({
  identifier: pkg.identifier,
  priceString: priceStringOf(pkg),
  native: pkg,
});

/**
 * Map a credit-pack package, reading its FYT amount from the credits offering's
 * `metadata` (a `{ <package lookup_key>: <credits> }` map). Keeps the amount in
 * RevenueCat rather than the app, so adding a pack needs no code change.
 */
const toCreditPackage = (
  pkg: Package,
  metadata: { [key: string]: unknown },
): SubscriptionPackage => {
  const credits = Number(metadata[pkg.identifier]);
  return {
    ...toSubscriptionPackage(pkg),
    credits: Number.isFinite(credits) ? credits : undefined,
  };
};

/**
 * Web (Expo web) purchases provider backed by `@revenuecat/purchases-js` +
 * RevenueCat Web Billing. Native builds resolve `Purchases.tsx` instead (Metro
 * platform resolution). Test mode uses the RC Billing sandbox key; prod uses the
 * production key (see `env.revenueCatWebApiKey`).
 */
export const PurchasesProvider = ({ children }: { children?: ReactNode }) => {
  const [isReady, setIsReady] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [creditPackages, setCreditPackages] = useState<SubscriptionPackage[]>(
    [],
  );
  const configuredRef = useRef(false);

  const authTokens = useReactiveVar(authVar);
  const { data } = useQuery(CurrentUserDocument, { skip: !authTokens });
  const userId = data?.currentUser?.id ?? null;

  const loadOfferings = useCallback(async () => {
    try {
      const { current, all } =
        await Purchases.getSharedInstance().getOfferings();
      setPackages(
        (current?.availablePackages ?? []).map(toSubscriptionPackage),
      );
      const credits = all[CREDITS_OFFERING];
      setCreditPackages(
        (credits?.availablePackages ?? []).map((pkg) =>
          toCreditPackage(pkg, credits?.metadata ?? {}),
        ),
      );
    } catch (e) {
      logger.error(e, '[Purchases] Failed to load offerings');
    }
  }, []);

  // Configure the SDK once on mount. purchases-js requires an app user id up front;
  // start anonymous when the logged-in id isn't known yet and reconcile below.
  useEffect(() => {
    const apiKey = env.revenueCatWebApiKey;
    if (!apiKey) {
      logger.warn(
        '[Purchases] No RevenueCat web API key configured; purchases disabled',
      );
      setIsReady(true);
      return;
    }
    void (async () => {
      try {
        if (!Purchases.isConfigured()) {
          Purchases.configure({
            apiKey,
            appUserId:
              userId ?? Purchases.generateRevenueCatAnonymousAppUserId(),
          });
        }
        configuredRef.current = true;
        setCustomerInfo(await Purchases.getSharedInstance().getCustomerInfo());
        await loadOfferings();
      } catch (e) {
        logger.error(e, '[Purchases] Configuration failed');
      } finally {
        setIsReady(true);
      }
    })();
    // Configure exactly once; identity changes are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the RevenueCat app user id in sync with the logged-in user.
  useEffect(() => {
    if (!configuredRef.current) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const instance = Purchases.getSharedInstance();
        const current = instance.getAppUserId();
        if (userId) {
          if (current === userId) {
            return;
          }
          const info = await instance.changeUser(userId);
          if (!cancelled) {
            setCustomerInfo(info);
          }
        } else if (!authTokens) {
          const anon = Purchases.generateRevenueCatAnonymousAppUserId();
          if (current === anon) {
            return;
          }
          const info = await instance.changeUser(anon);
          if (!cancelled) {
            setCustomerInfo(info);
          }
        }
      } catch (e) {
        logger.error(e, '[Purchases] Identity sync failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, authTokens, isReady]);

  const refresh = useCallback(async () => {
    if (!configuredRef.current) {
      return;
    }
    try {
      setCustomerInfo(await Purchases.getSharedInstance().getCustomerInfo());
    } catch (e) {
      logger.error(e, '[Purchases] refresh failed');
    }
    await loadOfferings();
  }, [loadOfferings]);

  const purchasePackage = useCallback(
    async (pkg: SubscriptionPackage): Promise<PurchaseResult> => {
      if (!configuredRef.current) {
        return { success: false, userCancelled: false };
      }
      try {
        const { customerInfo: info } =
          await Purchases.getSharedInstance().purchase({
            rcPackage: pkg.native as Package,
          });
        setCustomerInfo(info);
        return { success: true, userCancelled: false };
      } catch (e) {
        if (e instanceof PurchasesError) {
          if (e.errorCode === ErrorCode.UserCancelledError) {
            return { success: false, userCancelled: true };
          }
          // Already entitled — treat as success and re-sync the cached state.
          if (e.errorCode === ErrorCode.ProductAlreadyPurchasedError) {
            setCustomerInfo(
              await Purchases.getSharedInstance().getCustomerInfo(),
            );
            return { success: true, userCancelled: false };
          }
        }
        logger.error(e, '[Purchases] purchase failed');
        throw e;
      }
    },
    [],
  );

  // Web Billing has no store-level "restore"; re-fetch entitlements instead.
  const restorePurchases = useCallback(async (): Promise<void> => {
    await refresh();
  }, [refresh]);

  const openManageSubscriptions = useCallback(async (): Promise<void> => {
    const url = customerInfo?.managementURL;
    if (url && typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener');
    }
  }, [customerInfo]);

  const getPackage = useCallback(
    (lookupKey: string) =>
      packages.find((pkg) => pkg.identifier === lookupKey) ?? null,
    [packages],
  );

  const hasEntitlement = useCallback(
    (id: string) => !!customerInfo?.entitlements.active[id],
    [customerInfo],
  );

  // Mirror the resolved tier to the PowerSync connector (a non-React class) so it can pass
  // the interim `tier` sync parameter that bounds Free users to 30 days of history.
  useEffect(() => {
    tierVar(resolveTier(Object.keys(customerInfo?.entitlements.active ?? {})));
  }, [customerInfo]);

  const value = useMemo<PurchasesContextValue>(
    () => ({
      isReady,
      isPro: !!customerInfo?.entitlements.active[ENTITLEMENTS.pro],
      hasAiCoach: !!customerInfo?.entitlements.active[ENTITLEMENTS.ai],
      isUnlimited: !!customerInfo?.entitlements.active[ENTITLEMENTS.unlimited],
      hasRenewingSubscription: Object.values(
        customerInfo?.entitlements.active ?? {},
      ).some((e) => e.willRenew),
      hasEntitlement,
      activeEntitlements: Object.keys(customerInfo?.entitlements.active ?? {}),
      canManageSubscriptions: !!customerInfo?.managementURL,
      openManageSubscriptions,
      packages,
      creditPackages,
      getPackage,
      purchasePackage,
      restorePurchases,
      refresh,
    }),
    [
      isReady,
      customerInfo,
      hasEntitlement,
      openManageSubscriptions,
      packages,
      creditPackages,
      getPackage,
      purchasePackage,
      restorePurchases,
      refresh,
    ],
  );

  return (
    <purchasesContext.Provider value={value}>
      {children}
    </purchasesContext.Provider>
  );
};
